import gsap from "gsap";
import { ScrollTrigger } from "gsap/all";
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import { Autoplay, FreeMode } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

gsap.registerPlugin(ScrollTrigger);

const HalloweenMachine = forwardRef(({ gameResult, animateColumn, stopMachineFlag }, ref) => {
    const [textContent, setTextContent] = useState("How will you do?");
    const [highlightCoordinates, setHighlightCoordinates] = useState([]);
    const [highlightReady, setHighlightReady] = useState(false);
    const [randomGenerateEmoji, setRandomGenerateEmoji] = useState([]);

    useEffect(() => {
        let dummyArr = [];
        for (let i = 0; i < 5; i++) {
            dummyArr = getRandomEmojis(i, dummyArr);
        }
        setRandomGenerateEmoji(dummyArr);
    }, []);

    useEffect(() => {
        setHighlightReady(false);
        setHighlightCoordinates([]);
        if (gameResult) {
            highlightPaylines(gameResult.payLines);
        }
    }, [gameResult]);

    useEffect(() => {
        const random1 = gsap.utils.random(0, 360, 36);
        const random2 = gsap.utils.random(0, 360, 36);
        const random3 = gsap.utils.random(0, 360, 36);
        const random4 = gsap.utils.random(0, 360, 36);
        const random5 = gsap.utils.random(0, 360, 36);

        gsap?.timeline({ onComplete: finishScroll })
            ?.set(".ring", { rotationX: -90 })
            ?.set(".item", {
                rotateX: (i) => i * -36,
                transformOrigin: "50% 50% -220px",
                z: 220,
            })
            ?.to("#ring1", { rotationX: random1, duration: 1, ease: "power3" }, "<")
            ?.to("#ring2", { rotationX: random2, duration: 1.5, ease: "power3" }, "<")
            ?.to("#ring3", { rotationX: random3, duration: 2, ease: "power3" }, "<")
            ?.to("#ring4", { rotationX: random4, duration: 2.5, ease: "power3" }, "<")
            ?.to("#ring5", { rotationX: random5, duration: 3, ease: "power3" }, "<");

        highlightPaylines(gameResult?.payLines || []);
    }, [gameResult]);

    useEffect(() => {
        let timer = null;
        if (highlightCoordinates.length > 0) {
            if (stopMachineFlag) {
                setHighlightReady(true);
            } else {
                timer = setTimeout(() => {
                    setHighlightReady(true);
                }, 3500);
            }

            return () => {
                if (timer) {
                    clearTimeout(timer);
                }
            }
        }
    }, [highlightCoordinates, stopMachineFlag]);

    const highlightPaylines = (payLines) => {
        if (!payLines) return;

        const coordinates = [];
        payLines.forEach((payLine) => {
            payLine.line.forEach((coordinate) => {
                if (coordinate.isValidToDrawLine) {
                    coordinates.push(coordinate);
                }
            });
        });
        setHighlightCoordinates(coordinates);
    };

    const isHighlighted = (x, y) => {
        return highlightReady && highlightCoordinates.some((coordinate) => coordinate.x === x && coordinate.y === y);
    };

    const finishScroll = () => {
        const items = document.querySelectorAll(".item");
        items.forEach((item) => {
            ScrollTrigger.create({
                trigger: item,
                scroller: ".board-outer",
                start: "top center-=59px",
                end: "bottom center-=59px",
                toggleClass: "active",
            });
        });

        // const activeItem1 = document.querySelector("#col1 .item.active")?.dataset.content;
        // const activeItem2 = document.querySelector("#col2 .item.active")?.dataset.content;
        // const activeItem3 = document.querySelector("#col3 .item.active")?.dataset.content;
        // const activeItem4 = document.querySelector("#col4 .item.active")?.dataset.content;
        // const activeItem5 = document.querySelector("#col5 .item.active")?.dataset.content;
        // if (activeItem1 && activeItem2 && activeItem3 && activeItem4 && activeItem5) {
        //     if (activeItem1 === activeItem2 && activeItem2 === activeItem3 && activeItem3 === activeItem4 && activeItem4 === activeItem5) {
        //         setTextContent(`Jackpot! Everyone gets ${activeItem1}s!`);
        //     } else if (
        //         activeItem1 !== activeItem2 &&
        //         activeItem2 !== activeItem3 &&
        //         activeItem3 !== activeItem4 &&
        //         activeItem4 !== activeItem5 &&
        //         activeItem1 !== activeItem3 &&
        //         activeItem1 !== activeItem4 &&
        //         activeItem1 !== activeItem5 &&
        //         activeItem2 !== activeItem4 &&
        //         activeItem2 !== activeItem5 &&
        //         activeItem3 !== activeItem5
        //     ) {
        //         setTextContent("Bad luck, you lost");
        //     } else {
        //         setTextContent(`Close but no ${activeItem1}s for you. Why not try again?`);
        //     }
        // }
    };

    useImperativeHandle(ref, () => ({
        spin,
    }));

    const spin = () => {
        setTextContent("round and round it goes...");
        const random1 = gsap.utils.random(0, 1440, 36);
        const random2 = gsap.utils.random(0, 1440, 36);
        const random3 = gsap.utils.random(0, 1440, 36);
        const random4 = gsap.utils.random(0, 1440, 36);
        const random5 = gsap.utils.random(0, 1440, 36);
        gsap.timeline({ paused: true, onComplete: finishScroll })
            .to("#ring1", { rotationX: random1, duration: 2, ease: "power3" }, "<")
            .to("#ring2", { rotationX: random2, duration: 2.5, ease: "power3" }, "<")
            .to("#ring3", { rotationX: random3, duration: 3, ease: "power3" }, "<")
            .to("#ring4", { rotationX: random4, duration: 3.5, ease: "power3" }, "<")
            .to("#ring5", { rotationX: random5, duration: 4, ease: "power3" }, "<")
            .play();
    };

    const getEmoji = (name) => {
        const emojis = {
            diabo: <img className="img-fluid" src="assets/img/diabo.png" />,
            vampiro: <img className="img-fluid" src="assets/img/vampire.png" />,
            fantasma: <img className="img-fluid" src="assets/img/fantasma.png" />,
            espantalho: <img className="img-fluid" src="assets/img/espanthalho.png" />,
            abobora: <img className="img-fluid" src="assets/img/abobora.png" />,
            frankenstein: <img className="img-fluid" src="assets/img/frankenstein.png" />,
            calderao: <img className="img-fluid" src="assets/img/calderao.png" />,
            bruxa: <img className="img-fluid" src="assets/img/bruxa.png" />,
            bruxinha: <img className="img-fluid" src="assets/img/bruxinha.png" />,
            magica: <img className="img-fluid" src="assets/img/magica.png" />,
        };
        return emojis[name];
    };

    const character = (name) => {
        const emoji = {
            bulerBiabo: <img className="img-fluid" src="assets/img/sliderImage/abobora.png" />,
            bulerVampiro: <img className="img-fluid" src="assets/img/sliderImage/vampiro.png" />,
            bulerFantasma: <img className="img-fluid" src="assets/img/sliderImage/fantasma.png" />,
            bulerEspantalho: <img className="img-fluid" src="assets/img/sliderImage/espantalho.png" />,
            bulerFrankenstein: <img className="img-fluid" src="assets/img/sliderImage/frankenstein.png" />,
            bulerCalderao: <img className="img-fluid" src="assets/img/sliderImage/calderao.png" />,
            bulerBruxa: <img className="img-fluid" src="assets/img/sliderImage/bruxa.png" />,
            bulerBruxinha: <img className="img-fluid" src="assets/img/sliderImage/bruxinha.png" />,
            bulerDiabo: <img className="img-fluid" src="assets/img/sliderImage/diabo.png" />,
        };
        return emoji[name];
    };

    const getRandomEmojis = (index, emptyArr) => {
        const emojis = {
            diabo: <img className="img-fluid" src="assets/img/diabo.png" />,
            vampiro: <img className="img-fluid" src="assets/img/vampire.png" />,
            fantasma: <img className="img-fluid" src="assets/img/fantasma.png" />,
            espantalho: <img className="img-fluid" src="assets/img/espanthalho.png" />,
            abobora: <img className="img-fluid" src="assets/img/abobora.png" />,
            frankenstein: <img className="img-fluid" src="assets/img/frankenstein.png" />,
            calderao: <img className="img-fluid" src="assets/img/calderao.png" />,
            bruxa: <img className="img-fluid" src="assets/img/bruxa.png" />,
            bruxinha: <img className="img-fluid" src="assets/img/bruxinha.png" />,
            magica: <img className="img-fluid" src="assets/img/magica.png" />,
        };
        const characters = ["diabo", "vampiro", "fantasma", "espantalho", "abobora", "frankenstein", "calderao", "bruxa", "bruxinha", "magica"];
        const result = [];
        for (let i = 0; i < 3; i++) {
            const randomIndex = Math.floor(Math.random() * characters.length);
            result.push(characters[randomIndex]);
            characters.splice(randomIndex, 1);
        }

        const newArr = [emojis[result[0]], emojis[result[1]], emojis[result[2]]];
        emptyArr.push(newArr);
        return emptyArr;
    };

    const getSwipeAnimation = () => {
        return <Swiper direction="vertical" slidesPerView={1} spaceBetween={0} loop={true}
            freeMode={true} speed={20} className="mySwiper" grabCursor={true}
            autoplay={{
                delay: 5, reverseDirection: true, disableOnInteraction: false,
            }} breakpoints={{
                640: { slidesPerView: 1, spaceBetween: 0, },
                768: { slidesPerView: 1, spaceBetween: 0, },
                1024: { slidesPerView: 1, spaceBetween: 0, }
            }}
            modules={[Autoplay, FreeMode]}>
            <SwiperSlide><img src="assets/img/sliderImage/0.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/1.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/2.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/3.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/4.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/5.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/6.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/7.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/8.jpg" alt="" /></SwiperSlide>
            <SwiperSlide>{" "}<img src="assets/img/sliderImage/9.jpg" alt="" /></SwiperSlide>
        </Swiper>;
    }

    const getColumn = (index) => {
        return <>
            <div id={`col${index + 1}`} className={`col wheel`}>
                {(animateColumn && !animateColumn[index]) ? (
                    !gameResult ? (
                        randomGenerateEmoji.length > 0 && (
                            <div className="wheelContent">
                                <div className="wheelBox">{randomGenerateEmoji[index][0]}</div>
                                <div className="wheelBox">{randomGenerateEmoji[index][1]}</div>
                                <div className="wheelBox">{randomGenerateEmoji[index][2]}</div>
                            </div>
                        )
                    ) : (
                        <div className="wheelContent">
                            <div className={`wheelBox ${isHighlighted(0, index) ? "matchbox" : ""}`}>{getEmoji(gameResult?.resultMatrix[0][index]?.title)}</div>
                            <div className={`wheelBox ${isHighlighted(1, index) ? "matchbox" : ""}`}>{getEmoji(gameResult?.resultMatrix[1][index]?.title)}</div>
                            <div className={`wheelBox ${isHighlighted(2, index) ? "matchbox" : ""}`}>{getEmoji(gameResult?.resultMatrix[2][index]?.title)}</div>
                        </div>
                    )
                ) : (
                    getSwipeAnimation()
                )}
            </div>
        </>
    }

    return (
        <div className={`stage notstarted game-play h-100`}>
            {/*  ${col5 ? "dummyClass" : ""} */}
            <div className="board-row board">
                <div className="board-outer">
                    {getColumn(0)}
                    {getColumn(1)}
                    {getColumn(2)}
                    {getColumn(3)}
                    {getColumn(4)}
                </div>
            </div>
        </div>
    );
});

export default HalloweenMachine;